import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  getCurrentPlayingMessageIndex,
  getValidAudioDuration,
  scrollToPlayingAudioTranscriptMessage,
} from '@modules/feedback/components/AudioType/utils';
import useFeedbackListStore, {
  FeedbackListStore,
} from '@modules/feedback/store/useFeedbackListStore';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import { useMount } from 'react-use';
import { AudioConversationMessage } from 'v2/core/entities/feedback/content';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';

const getFeedbackListStoreState = (
  state: FeedbackListStore,
  feedbackID: UUID,
) => {
  return {
    isPlaying: state.feedbacks[feedbackID]?.isPlaying ?? false,
    setIsPlaying: state.setIsPlaying,
    setDuration: state.setAudioDuration,
    setSeek: state.setAudioSeek,
    setRate: state.setAudioRate,
    setSnippetTimeToPlay: state.setSnippetTimeToPlay,
    setSummaryTimeToPlay: state.setSummaryTimeToPlay,
    setIsTranscriptPopoverOpened: state.setIsTranscriptPopoverOpened,
    setIsTranscriptPopoverExpanded: state.setIsTranscriptPopoverExpanded,
    setCurrentPlayingMsgIndex: state.setCurrentPlayingMsgIndex,
    isTranscriptPopoverExpanded:
      state.feedbacks[feedbackID]?.isTranscriptPopoverExpanded ?? false,
    duration: state.feedbacks?.[feedbackID]?.audioDuration ?? 0,
    seek: state.feedbacks?.[feedbackID]?.audioSeek ?? 0,
    rate: state.feedbacks?.[feedbackID]?.audioRate ?? 1,
    snippetTimeToPlay: state.feedbacks?.[feedbackID]?.snippetTimeToPlay,
    summaryTimeToPlay: state.feedbacks?.[feedbackID]?.summaryTimeToPlay,
  };
};

interface AudioFeedbackPlayerControlsParams {
  feedbackID: UUID;
  audioElementID: string;
  jumpBufferTime: number;
  audioConversation: AudioConversationMessage[];
  shouldAutoPause: boolean;
  isEmptyAudioSource: boolean;
}

export const useAudioFeedbackPlayerControls = ({
  feedbackID,
  audioElementID,
  jumpBufferTime,
  audioConversation,
  shouldAutoPause,
  isEmptyAudioSource,
}: AudioFeedbackPlayerControlsParams) => {
  const { showErrorNotification } = useNotificationContext();

  const {
    duration,
    seek,
    rate,
    isPlaying,
    setIsPlaying,
    snippetTimeToPlay,
    summaryTimeToPlay,
    isTranscriptPopoverExpanded,
    setSummaryTimeToPlay,
    setDuration,
    setSeek,
    setRate,
    setSnippetTimeToPlay,
    setIsTranscriptPopoverOpened,
    setIsTranscriptPopoverExpanded,
    setCurrentPlayingMsgIndex,
  } = useFeedbackListStore(
    (state) => getFeedbackListStoreState(state, feedbackID),
    shallow,
  );

  const audioRef = useRef<ReactAudioPlayer | null>(null);
  const dingTimeoutRef = useRef(0);

  const [localRate, setLocalRate] = useState(rate);
  const [canPlay, setCanPlay] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isError, setIsError] = useState(false);
  const [shouldLoadAudio, setShouldLoadAudio] = useState(false);

  useMount(() => {
    setDuration(getValidAudioDuration(duration, audioConversation), feedbackID);
  });

  // reset state when feedback view changes from card to list or vice versa
  useEffect(() => {
    if (isPlaying) {
      setIsPlaying(false, feedbackID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoPause]);

  const handleOnLoadedMetadata = useCallback(() => {
    if (!audioRef.current?.audioEl.current) {
      return;
    }
    setDuration(audioRef.current.audioEl.current.duration, feedbackID);
  }, [feedbackID, setDuration]);

  const handlePauseOtherAudioElements = useCallback(() => {
    requestAnimationFrame(() => {
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach((audioEl) => {
        /**
         * Context: `shouldAutoPause` is true for a Feedback card in list view only when
         * the Feedback Detail Drawer is open
         *
         * if `shouldAutoPause` is true, then pause all audio element (from Feedback card in list view)
         *
         * if `shouldAutoPause` is false, when
         * either Feedback Detail Drawer is closed
         * or the current audio element is from Feedback Detail Drawer
         * then, pause all audio other elements
         */
        const shouldPauseOtherAudioEls = shouldAutoPause
          ? audioEl.id === audioElementID
          : audioEl.id !== audioElementID;

        if (shouldPauseOtherAudioEls && !audioEl.paused && !audioEl.ended) {
          audioEl.pause();
        }
      });
    });
  }, [audioElementID, shouldAutoPause]);

  const handleResetStateOnSeekChange = useCallback(
    (
      time: { startTime: number; endTime: number } | undefined,
      currentSeek: number,
      onReset: () => void,
    ) => {
      if (!time) {
        return;
      }

      const formattedCurrentSeek = parseFloat(currentSeek.toFixed(2));
      const formattedStartTime = parseFloat(time.startTime.toFixed(2));
      const formattedEndTime = parseFloat(time.endTime.toFixed(2));

      if (
        formattedCurrentSeek < formattedStartTime ||
        formattedCurrentSeek > formattedEndTime
      ) {
        onReset();
      }
    },
    [],
  );

  const handleOnJumpSeek = useCallback(
    (time: number) => {
      if (!audioRef.current?.audioEl.current) {
        return;
      }

      setSeek(time, feedbackID);
      audioRef.current.audioEl.current.currentTime = time;

      // reset play sharing/shared snippet state, when seek goes out of range
      handleResetStateOnSeekChange(snippetTimeToPlay, time, () => {
        setSnippetTimeToPlay(undefined, feedbackID);
      });
      // reset play summary state., when seek goes out of range
      handleResetStateOnSeekChange(summaryTimeToPlay, time, () => {
        setSummaryTimeToPlay(undefined, feedbackID);
      });
    },
    [
      feedbackID,
      handleResetStateOnSeekChange,
      setSeek,
      setSnippetTimeToPlay,
      setSummaryTimeToPlay,
      snippetTimeToPlay,
      summaryTimeToPlay,
    ],
  );

  const handleOnTogglePlay = useCallback(
    (options?: { shouldSyncSeekValue: boolean }) => {
      if (isEmptyAudioSource) {
        return;
      }

      const shouldSyncSeekValue = options?.shouldSyncSeekValue ?? true;
      if (!audioRef.current?.audioEl.current) {
        return;
      }

      if (isPlaying) {
        audioRef.current.audioEl.current.pause();
        return;
      }

      if (
        shouldSyncSeekValue &&
        seek !== audioRef.current.audioEl.current.currentTime
      ) {
        // jump to seek, if 'seek' state is not synced with 'currentTime'
        handleOnJumpSeek(seek);
      }

      if (!shouldLoadAudio) {
        setShouldLoadAudio(true);
      }
      handlePauseOtherAudioElements();
      audioRef.current.audioEl.current.play();
    },
    [
      handleOnJumpSeek,
      handlePauseOtherAudioElements,
      isEmptyAudioSource,
      isPlaying,
      seek,
      shouldLoadAudio,
    ],
  );

  const handleOnPlay = useCallback(() => {
    if (!shouldAutoPause) {
      setIsPlaying(true, feedbackID);
    }
  }, [feedbackID, setIsPlaying, shouldAutoPause]);

  const handleOnPause = useCallback(() => {
    if (!shouldAutoPause) {
      setIsPlaying(false, feedbackID);
    }
  }, [feedbackID, setIsPlaying, shouldAutoPause]);

  const handleOnCanPlay = useCallback(() => {
    setCanPlay(true);
  }, []);

  const handleOnJumpSeekWithBuffer = useCallback(
    (time: number) => {
      const calculatedSeek = time - jumpBufferTime;
      const newSeek = calculatedSeek < 0 ? 0 : calculatedSeek;
      handleOnJumpSeek(newSeek);
    },
    [handleOnJumpSeek, jumpBufferTime],
  );

  const handleOnRewind = useCallback(() => {
    if (!audioRef.current?.audioEl.current) {
      return;
    }
    const calculatedSeek = audioRef.current?.audioEl.current.currentTime - 10;
    const newSeek = calculatedSeek < 0 ? 0 : calculatedSeek;
    handleOnJumpSeek(newSeek);
  }, [handleOnJumpSeek]);

  const handleOnForward = useCallback(() => {
    if (!audioRef.current?.audioEl.current) {
      return;
    }
    const calculatedSeek = audioRef.current?.audioEl.current.currentTime + 10;
    const newSeek = calculatedSeek > duration ? duration : calculatedSeek;
    handleOnJumpSeek(newSeek);
  }, [duration, handleOnJumpSeek]);

  const handleOnChangeRate = useCallback(
    (rate: number) => {
      if (
        !audioRef.current?.audioEl.current ||
        rate === audioRef.current.audioEl.current.playbackRate
      ) {
        return;
      }
      audioRef.current.audioEl.current.playbackRate = rate;
      setRate(rate, feedbackID);
      setLocalRate(rate);
    },
    [feedbackID, setRate],
  );

  const handleOnPlayDingSound = useCallback(() => {
    if (isEmptyAudioSource) {
      return;
    }

    const dingSound = new Audio('/ding.mp3');
    window.clearTimeout(dingTimeoutRef.current);
    dingTimeoutRef.current = window.setTimeout(() => {
      dingSound.play();
    }, 500);
  }, [isEmptyAudioSource]);

  const handleOnJumpToBookmark = useCallback(
    (time: number, shouldBuffer: boolean) => {
      if (shouldBuffer) {
        handleOnJumpSeekWithBuffer(time);
      } else {
        handleOnJumpSeek(time);
      }
      handleOnPlayDingSound();

      if (!isPlaying) {
        handleOnTogglePlay({ shouldSyncSeekValue: false });
      }

      // scroll to playing message
      if (isTranscriptPopoverExpanded) {
        const currentPlayingMsgIndex = getCurrentPlayingMessageIndex(
          audioConversation,
          time,
        );
        setCurrentPlayingMsgIndex(currentPlayingMsgIndex, feedbackID);
        scrollToPlayingAudioTranscriptMessage(currentPlayingMsgIndex);
      }

      // expand transcript popover, if it's not expanded (only for empty audio source)
      if (isEmptyAudioSource && !isTranscriptPopoverExpanded) {
        setIsTranscriptPopoverExpanded(true, feedbackID);
      }
    },
    [
      audioConversation,
      feedbackID,
      handleOnJumpSeek,
      handleOnJumpSeekWithBuffer,
      handleOnPlayDingSound,
      handleOnTogglePlay,
      isEmptyAudioSource,
      isPlaying,
      isTranscriptPopoverExpanded,
      setCurrentPlayingMsgIndex,
      setIsTranscriptPopoverExpanded,
    ],
  );

  const handleOnChangeSeekBar = useCallback(
    (time: number | number[]) => {
      setSeek(time as number, feedbackID);
      setIsSeeking(true);

      if (isTranscriptPopoverExpanded) {
        const currentPlayingMsgIndex = getCurrentPlayingMessageIndex(
          audioConversation,
          time as number,
        );
        setCurrentPlayingMsgIndex(currentPlayingMsgIndex, feedbackID);
        scrollToPlayingAudioTranscriptMessage(currentPlayingMsgIndex);
      }
    },
    [
      audioConversation,
      feedbackID,
      isTranscriptPopoverExpanded,
      setCurrentPlayingMsgIndex,
      setSeek,
    ],
  );

  const handleOnCommitSeekBar = useCallback(
    (time: number | number[]) => {
      handleOnJumpSeek(time as number);
      setIsSeeking(false);
    },
    [handleOnJumpSeek],
  );

  const handleOnListen = useCallback(() => {
    if (!audioRef.current?.audioEl.current) {
      return;
    }
    const currentTime = audioRef.current.audioEl.current.currentTime;
    setSeek(currentTime, feedbackID);

    // pause play shared/sharing snippet state, when it's finished
    handleResetStateOnSeekChange(snippetTimeToPlay, currentTime, () => {
      setSnippetTimeToPlay(undefined, feedbackID);
      if (isPlaying) {
        handleOnTogglePlay();
      }
    });

    // pause play summary state, when it's finished
    handleResetStateOnSeekChange(summaryTimeToPlay, currentTime, () => {
      setSummaryTimeToPlay(undefined, feedbackID);
      if (isPlaying) {
        handleOnTogglePlay();
      }
    });
  }, [
    feedbackID,
    handleOnTogglePlay,
    handleResetStateOnSeekChange,
    isPlaying,
    setSeek,
    setSnippetTimeToPlay,
    setSummaryTimeToPlay,
    snippetTimeToPlay,
    summaryTimeToPlay,
  ]);

  const handleOnResetStates = useCallback(
    ({
      skipTranscriptPopoverState,
    }: {
      skipTranscriptPopoverState?: boolean;
    } = {}) => {
      handlePauseOtherAudioElements();
      setIsPlaying(false, feedbackID);
      setDuration(0, feedbackID);
      setCanPlay(false);
      setSnippetTimeToPlay(undefined, feedbackID);
      handleOnChangeRate(localRate);
      setIsTranscriptPopoverOpened(true, feedbackID);
      if (!skipTranscriptPopoverState) {
        setIsTranscriptPopoverExpanded(false, feedbackID);
      }
    },
    [
      handlePauseOtherAudioElements,
      setIsPlaying,
      setDuration,
      feedbackID,
      setSnippetTimeToPlay,
      handleOnChangeRate,
      localRate,
      setIsTranscriptPopoverOpened,
      setIsTranscriptPopoverExpanded,
    ],
  );

  const handleOnError = useCallback(() => {
    showErrorNotification({
      contentComponent: `Failed to load audio for feedback: ${feedbackID}.`,
      props: {},
    });
    setIsError(true);
  }, [feedbackID, showErrorNotification]);

  return {
    audioRef,
    states: {
      canPlay,
      rate: localRate,
      isPlaying,
      isSeeking,
      duration,
      seek,
      isError,
      shouldLoadAudio,
    },
    handlers: {
      handleOnLoadedMetadata,
      handleOnJumpSeek,
      handleOnJumpSeekWithBuffer,
      handleOnTogglePlay,
      handleOnCanPlay,
      handleOnPlay,
      handleOnPause,
      handleOnListen,
      handleOnRewind,
      handleOnForward,
      handleOnChangeRate,
      handleOnJumpToBookmark,
      handleOnChangeSeekBar,
      handleOnCommitSeekBar,
      handleOnError,
      handleOnResetStates,
    },
  };
};
